import {
  useQuery,
  UseQueryOptions,
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { useContext } from "react";
import { AppContext } from "../context/app";
import {
  getUserProfileByUserUuid,
  QuickAction,
  getQuickActions,
  getQuickActionsList,
  toggleQuickAction,
  ToggleQuickActionParams,
  updateQuickActionPositions,
  UpdateQuickActionPositionsParams,
  resetQuickActions,
  QuickActionBatchUpdateResponse,
  PinnedDateSettings,
  pinDateSettings,
  getPinnedDateSettings,
  updateClientUserProfile,
  unblockUser,
  blockUser,
  getUserDocuments,
  getClientDocuments,
  createUserDocument,
  CreateUserDocumentBody,
  ClientDocument,
  UserDocument,
  revokeUserDocument,
  getUserAndLeadNotes,
  createUserNote,
  deleteNote,
  CreateNoteBody,
  patchUser,
} from "./userApi";
import type { UpdateProfileData } from "../pages/Users/types";
import { getUsersInfo } from "./profileApi";

export function useProfileQuery(userUuid: string) {
  const app = useContext(AppContext);
  return useQuery({
    queryKey: ["client-user-profile", userUuid],
    queryFn: () => getUserProfileByUserUuid(userUuid, app.client.uuid),
  });
}

export function useGetUsersInfo() {
  return useQuery({
    queryKey: ["profile-users-info"],
    queryFn: () => getUsersInfo(),
  });
}

export function useUpdateClientUserProfile() {
  return useMutation({
    mutationFn: (payload: {
      clientUserUuid: string;
      data: UpdateProfileData[];
    }) => updateClientUserProfile(payload.clientUserUuid, payload.data),
  });
}

export function useUpdateBlockUser() {
  return useMutation({
    mutationFn: (payload: { isBlocked: boolean; userUuid: string }) =>
      payload.isBlocked
        ? unblockUser(payload.userUuid)
        : blockUser(payload.userUuid),
  });
}

export function useUpdateUser() {
  return useMutation({
    mutationFn: (payload: {
      userUuid: string;
      data: { parentUserId?: number; email?: string };
    }) => patchUser(payload.userUuid, payload.data),
  });
}

export function useQuickActionsQuery(options?: UseQueryOptions<QuickAction[]>) {
  return useQuery<QuickAction[]>({
    ...options,
    queryKey: ["quick-actions"],
    queryFn: () => getQuickActions(),
  });
}

export function useQuickActionsListQuery(
  options?: UseQueryOptions<QuickAction[]>,
) {
  return useQuery<QuickAction[]>({
    ...options,
    queryKey: ["quick-actions-list"],
    queryFn: () => getQuickActionsList(),
  });
}

export function useToggleQuickActionMutation(
  options?: UseMutationOptions<
    { data: string },
    unknown,
    ToggleQuickActionParams
  >,
) {
  const queryClient = useQueryClient();

  return useMutation<{ data: string }, unknown, ToggleQuickActionParams>({
    ...options,
    mutationFn: (params) => toggleQuickAction(params),
    onSuccess: (data, variables, context) => {
      // Invalidate the quick-actions queries to refetch data
      queryClient.invalidateQueries({ queryKey: ["quick-actions"] });
      queryClient.invalidateQueries({ queryKey: ["quick-actions-list"] });

      // Call the user-provided onSuccess if it exists
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}

export function useUpdateQuickActionPositionsMutation(
  options?: UseMutationOptions<
    QuickActionBatchUpdateResponse,
    unknown,
    UpdateQuickActionPositionsParams
  >,
) {
  const queryClient = useQueryClient();

  return useMutation<
    QuickActionBatchUpdateResponse,
    unknown,
    UpdateQuickActionPositionsParams
  >({
    ...options,
    mutationFn: (params) => updateQuickActionPositions(params),
    onSuccess: (data, variables, context) => {
      // Invalidate the quick-actions queries to refetch data
      queryClient.invalidateQueries({ queryKey: ["quick-actions"] });
      queryClient.invalidateQueries({ queryKey: ["quick-actions-list"] });

      // Call the user-provided onSuccess if it exists
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}

export function useResetQuickActionsMutation(
  options?: UseMutationOptions<{ data: string }, unknown>,
) {
  const queryClient = useQueryClient();
  return useMutation<{ data: string }, unknown>({
    ...options,
    mutationFn: () => resetQuickActions(),
    onSuccess: () => {
      // Invalidate the quick-actions queries to refetch data
      queryClient.invalidateQueries({ queryKey: ["quick-actions"] });
      queryClient.invalidateQueries({ queryKey: ["quick-actions-list"] });
    },
  });
}

export function usePinnedDateSettingsQuery(
  options?: UseQueryOptions<PinnedDateSettings>,
) {
  return useQuery<PinnedDateSettings>({
    ...options,
    queryKey: ["pinned-date-settings"],
    queryFn: () => getPinnedDateSettings(),
  });
}

export function usePinDateSettingsMutation(
  options?: UseMutationOptions<void, unknown, PinnedDateSettings>,
) {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, PinnedDateSettings>({
    ...options,
    mutationFn: (settings) => pinDateSettings(settings),
    onSuccess: (data, variables, context) => {
      // Invalidate the pinned date settings query to refetch data
      queryClient.invalidateQueries({ queryKey: ["pinned-date-settings"] });

      // Call the user-provided onSuccess if it exists
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
}

export function useGetClientDocumentsQuery(
  clientUuid: string,
  options?: UseQueryOptions<
    ClientDocument[],
    Error,
    ClientDocument[],
    [string, string]
  >,
) {
  return useQuery({
    ...options,
    queryKey: ["client-documents", clientUuid],
    queryFn: () => getClientDocuments(clientUuid),
  });
}

export function useGetUserDocumentsQuery(
  userUuid: string,
  clientUuid: string,
  options?: UseQueryOptions<
    UserDocument[],
    Error,
    UserDocument[],
    [string, string, string]
  >,
) {
  return useQuery({
    ...options,
    queryKey: ["client-user-documents", clientUuid, userUuid],
    queryFn: () => getUserDocuments(clientUuid, userUuid),
  });
}

export function useCreateUserDocumentMutation(options?: UseQueryOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      userUuid: string;
      clientUuid: string;
      body: CreateUserDocumentBody;
    }) =>
      createUserDocument(payload.userUuid, payload.clientUuid, payload.body),
    onSuccess: (data, variables) => {
      // Invalidate the pinned date settings query to refetch data
      queryClient.invalidateQueries({
        queryKey: [
          "client-user-documents",
          variables.clientUuid,
          variables.userUuid,
        ],
      });
      // Call the user-provided onSuccess if it exists
      if (options?.onSuccess) {
        options.onSuccess(data);
      }
    },
  });
}

export function useRevokeUserDocumentMutation(options?: UseQueryOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { userDocumentId: string }) =>
      revokeUserDocument(payload.userDocumentId),
    onSuccess: (data) => {
      // Invalidate the pinned date settings query to refetch data
      queryClient.invalidateQueries({
        queryKey: ["client-user-documents"],
      });
      // Call the user-provided onSuccess if it exists
      if (options?.onSuccess) {
        options.onSuccess(data);
      }
    },
  });
}

export function useGetUserAndLeadNotes(
  userUuid: string,
  clientUuid: string,
  options?: UseQueryOptions,
) {
  return useQuery<any>({
    ...options,
    queryKey: ["user-notes", userUuid],
    queryFn: () => getUserAndLeadNotes(clientUuid, userUuid),
  });
}

export function useCreateUserNote(options?: UseQueryOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { userUuid: string; body: CreateNoteBody }) =>
      createUserNote(payload.userUuid, payload.body),
    onSuccess: (data) => {
      // Invalidate the pinned date settings query to refetch data
      queryClient.invalidateQueries({
        queryKey: ["user-notes"],
      });
      // Call the user-provided onSuccess if it exists
      if (options?.onSuccess) {
        options.onSuccess(data);
      }
    },
  });
}

export function useDeleteNote(options?: UseQueryOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { uuid: string }) => deleteNote(payload.uuid),
    onSuccess: (data) => {
      // Invalidate the pinned date settings query to refetch data
      queryClient.invalidateQueries({
        queryKey: ["user-notes"],
      });
      // Call the user-provided onSuccess if it exists
      if (options?.onSuccess) {
        options.onSuccess(data);
      }
    },
  });
}
